import { useEffect } from "react";
import { useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { useProperty } from "../lib/property-context";
import { Field, selectCls } from "./form-controls";

// Scope state: undefined === property-wide; otherwise a unit id.
export type UnitScope = string | undefined;

type ScopeContext = {
  role: "landlord" | "tenant";
  myUnits: { _id: string; label: string }[];
  allUnits: { _id: string; label: string }[];
};

// Scope picker shared by the FAQ, Q&A, and maintenance forms.
//
// Behavior by role:
// - Tenant: default = their unit. Single checkbox flips to
//   property-wide. If they have multiple active units (rare), shows a
//   small radio group instead of a checkbox.
// - Landlord: dropdown with "Property-wide" + each unit. Default =
//   property-wide (landlords have no local unit).
// - Loading / no membership: renders nothing; parent disables submit
//   until the context resolves.
//
// The parent owns the state and passes (value, onChange). The
// component is purely a controlled input.
export function UnitScopeField({
  value,
  onChange,
  label = "Scope",
  helperText,
}: {
  value: UnitScope;
  onChange: (next: UnitScope) => void;
  label?: string;
  helperText?: string;
}) {
  const property = useProperty();
  const ctx = useQuery(
    anyApi.units.scopeContextForProperty,
    property ? { propertySlug: property.slug } : "skip",
  ) as ScopeContext | null | undefined;

  // On first resolution, snap the value to the role-appropriate
  // default: tenants default to their (first) unit, landlords default
  // to property-wide. The parent's initial state is `undefined`
  // (= property-wide), which is correct for landlords but wrong for
  // tenants — this effect fixes that as soon as we know the role.
  useEffect(() => {
    if (!ctx) return;
    if (ctx.role === "tenant" && value === undefined && ctx.myUnits.length > 0) {
      onChange(ctx.myUnits[0]!._id);
    }
    // Landlord default is already property-wide (undefined); no-op.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx]);

  if (ctx === undefined) {
    return null;
  }
  if (ctx === null) {
    return null;
  }

  if (ctx.role === "tenant") {
    return (
      <TenantScope ctx={ctx} value={value} onChange={onChange} />
    );
  }

  // Landlord
  return (
    <Field label={label}>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || undefined)}
        className={selectCls}
      >
        <option value="">Property-wide</option>
        {ctx.allUnits.map((u) => (
          <option key={u._id} value={u._id}>
            {u.label}
          </option>
        ))}
      </select>
      {helperText && (
        <p className="mt-1 text-xs text-slate-500">{helperText}</p>
      )}
    </Field>
  );
}

function TenantScope({
  ctx,
  value,
  onChange,
}: {
  ctx: ScopeContext;
  value: UnitScope;
  onChange: (next: UnitScope) => void;
}) {
  const myUnits = ctx.myUnits;

  if (myUnits.length === 0) {
    // Tenant with no active tenancy — can only post property-wide
    // (this is a rare edge: a tenant whose tenancy ended but who
    // still has a membership row). Force value to undefined.
    if (value !== undefined) onChange(undefined);
    return (
      <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
        You don't have an active tenancy on this property — this will be
        posted property-wide.
      </p>
    );
  }

  // Common case: tenant with exactly one unit. Checkbox toggles
  // property-wide.
  if (myUnits.length === 1) {
    const unit = myUnits[0]!;
    const isPropertyWide = value === undefined;
    return (
      <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 cursor-pointer hover:bg-slate-100 transition-colors">
        <input
          type="checkbox"
          checked={isPropertyWide}
          onChange={(e) => onChange(e.target.checked ? undefined : unit._id)}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20"
        />
        <span className="text-sm text-slate-700">
          <span className="font-medium text-slate-900">Apply property-wide</span>
          <span className="block text-xs text-slate-500 mt-0.5">
            Otherwise this is only visible in your unit ({unit.label}).
          </span>
        </span>
      </label>
    );
  }

  // Rare edge: tenant occupying multiple units on the same property.
  // Show a small radio group with "Property-wide" + each unit.
  return (
    <Field label="Scope">
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || undefined)}
        className={selectCls}
      >
        <option value="">Property-wide</option>
        {myUnits.map((u) => (
          <option key={u._id} value={u._id}>
            {u.label}
          </option>
        ))}
      </select>
    </Field>
  );
}
